import {
  ActivatedRouteSnapshot,
  PreloadingStrategy,
  Route,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, delay, mergeMap } from 'rxjs/operators';

export class CustomPreloadingStrategy implements PreloadingStrategy {
  /**
   *
   * @param route
   * @param fn
   *
   * Custom preloading strategy, to avoid preloading all modules.
   * By default all modules are preloaded, with 5000ms delay
   *
   * If you do not wish to preload module, use `noPreload`
   *
   * ```ts
   * {
   *   path: 'my-path',
   *   data: { noPreload: true },
   *   loadChildren: () => ...,
   * },
   * ```
   *
   * If you wish to avoid delay in module preloading, use `noDelay`.
   * Delay has been added to free browser resources while initial content is being rendered.
   *
   * ```ts
   * {
   *   path: 'my-path',
   *   data: { noDelay: true },
   *   loadChildren: () => ...,
   * },
   * ```
   *
   */
  preload(route: Route, fn: () => Observable<any>): Observable<any> {
    const data = (route as ActivatedRouteSnapshot)?.data;

    if (data?.noPreload) {
      return of(null);
    }

    // We add default delay, but can turned off if needed
    const delayTime = data?.noDelay ? 0 : 5000;

    return of(null).pipe(
      delay(delayTime),
      mergeMap(() => fn()),
      catchError(() => of(null))
    );
  }
}
